import { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import PageScaffold from '../../components/PageScaffold';
import { SUBSCRIPTIONS_PAGE_PATH } from './SubscriptionsPage';
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Flex,
  Input,
  message,
  Modal,
  Select,
  Skeleton,
  Spin,
  Tabs,
  Tooltip,
} from 'antd';
import SubscriptionsContext from '../../contexts/data/SubscriptionsContext';
import { SubscriptionEditionFormProvider } from '../../contexts/forms/subscriptions/SubscriptionEditionFormContext';
import SubscriptionDetails from '../../components/subscription_detail_page/SubscriptionDetails';
import SubscriptionRelativesLinks from '../../components/subscription_detail_page/SubscriptionRelativesLinks';
import {
  BankOutlined,
  BarChartOutlined,
  BellOutlined,
  BulbOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ContactsOutlined,
  DesktopOutlined,
  DownloadOutlined,
  EuroCircleOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
  LineChartOutlined,
  MessageOutlined,
  PauseOutlined,
  PlusCircleOutlined,
  PushpinOutlined,
  ReloadOutlined,
  ScheduleOutlined,
  StarOutlined,
  TagsOutlined,
  UserAddOutlined,
  UserOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import SubscriptionCost from '../../components/subscription_detail_page/SubscriptionCost';
import JobsList from '../../components/@shared/JobsList';
import JobsContext from '../../contexts/data/JobsContext';
import SMSContext from '../../contexts/data/SMSContext';
import SMSList from '../../components/@shared/SMSList';
import NotificationsContext from '../../contexts/data/NotificationsContext';
import NotificationsList from '../../components/@shared/NotificationsList';
import { CONTACTS_PAGE_PATH } from '../contacts/ContactsPage';
import StatsTile from '../../components/@shared/StatsTile';
import { round2digits, formatCredit } from '../../../core/utils/commonUtils';
import SubscriptionUsersList from '../../components/subscription_detail_page/SubscriptionUsersList';
import ReactApexChart from 'react-apexcharts';
import {
  partnersTypeAsString,
  PartnersType,
  ContextStatus,
  creditTypeAsString,
  JobType,
  AIStatus,
  EProductType,
  TagCategory,
  SubscriptionStatus,
} from '@leedpulse-monorepo/enums';
import { formatNumber } from '@leedpulse-monorepo/utils';
import AIContext from '../../contexts/data/AIContext';
import dayjs, { Dayjs } from 'dayjs';
import PlansContext from '../../contexts/data/PlansContext';
import CRMDetails from './crm/CRMDetail';
import SubscriptionScenariosList from './scenarios/SubscriptionScenariosList';
import ScenariosContext from '../../contexts/data/ScenariosContext';
import { ApexOptions } from 'apexcharts';
import SubscriptionLogs from '../../components/subscription_detail_page/SubscriptionLogs';
import TagsList from '../../components/domains_page/TagsList';
import getCompaniesBySubscriptionId from '../../../domain/use_cases/companies/getCompaniesBySubscriptionId';
import { ICompany, ISubscription } from '@leedpulse-monorepo/interfaces';

const { RangePicker } = DatePicker;

interface SubscriptionDetailPageContentProps {
  subscriptionId: string;
}

export const SUBSCRIPTION_COST_TAB_KEY = '11';

const TAB_KEYS = {
  stats: '1',
  generalInformation: '2',
  users: '3',
  notifications: '4',
  scheduledMessages: '5',
  sms: '6',
  crm: '7',
  scenarios: '8',
  logs: '9',
  tags: '10',
  costs: SUBSCRIPTION_COST_TAB_KEY,
}

// Component to conditionnaly return if bad params
const SubscriptionDetailPage: React.FC = () => {
  const { subscriptionId } = useParams<{ subscriptionId: string }>();
  const navigate = useNavigate();

  if (!subscriptionId) {
    navigate(SUBSCRIPTIONS_PAGE_PATH);
    return;
  }

  return <SubscriptionDetailPageContent subscriptionId={subscriptionId} />;
};

const SubscriptionDetailPageContent: React.FC<
  SubscriptionDetailPageContentProps
> = ({ subscriptionId }) => {
  const subscriptionsContext = useContext(SubscriptionsContext);
  const jobsContext = useContext(JobsContext);
  const smsContext = useContext(SMSContext);
  const notificationsContext = useContext(NotificationsContext);
  const aiContext = useContext(AIContext);
  const plansContext = useContext(PlansContext);
  const scenariosContext = useContext(ScenariosContext);
  const navigate = useNavigate();

  const selectionMismatch =
    subscriptionsContext.selected === null ||
    subscriptionsContext.selected?.id !== subscriptionId;

  // States
  const [openModalAddUser, setOpenModalAddUser] = useState(false);
  const [emailAddUser, setEmailAddUser] = useState('');
  const [scenarioSelected, setScenarioSelected] = useState('');
  const [collaboratorSelected, setCollaboratorSelected] = useState('');
  const [senderSelected, setSenderSelected] = useState<string | null>(null);
  const [period, setPeriod] = useState<[Dayjs | null, Dayjs | null]>([
    null,
    null,
  ]);
  // Derive, pas d'etat : seul l'effet l'ecrivait. Il ne dependait que de
  // scenariosContext.scenarios alors qu'il lisait aussi idScenarios — la liste
  // restait donc perimee quand l'abonnement selectionne changeait seul.
  const subscriptionScenarios = subscriptionsContext.selected
    ? scenariosContext.scenarios.filter((scenario) =>
        (subscriptionsContext.selected?.idScenarios || []).includes(scenario.id)
      )
    : [];
  const [relatedCompany, setRelatedCompany] = useState<ICompany | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') ?? TAB_KEYS.stats);


  useEffect(() => {
    if (subscriptionsContext.selected?.id !== subscriptionId) {
      subscriptionsContext.fetchUsers(subscriptionId);
    }
    if (subscriptionsContext.selected) {
      scenariosContext.fetchByIds(subscriptionsContext.selected.idScenarios);
    }
  }, [subscriptionsContext.selected]);

  useEffect(() => {
    if (subscriptionsContext.statusAddRemoveUser === ContextStatus.SUCCESS) {
      message.success(
        <Flex align="center" gap={6}>Les utilisateurs de l'abonnement ont été mis à jour<CheckCircleOutlined /></Flex>
      );
      subscriptionsContext.fetchUsers(subscriptionId);
    }
    if (subscriptionsContext.statusAddRemoveUser === ContextStatus.ERROR) {
      message.error(subscriptionsContext.errorMessage);
      subscriptionsContext.fetchUsers(subscriptionId);
    }
  }, [subscriptionsContext.statusAddRemoveUser]);

  useEffect(() => {
    subscriptionsContext.fetchAndSelectById(
      subscriptionId,
      scenarioSelected,
      collaboratorSelected,
      period
    );
    jobsContext.fetchActionChart(scenarioSelected, collaboratorSelected, senderSelected);
  }, [scenarioSelected, collaboratorSelected, period, senderSelected]);

  useEffect(() => {
    if (
      selectionMismatch &&
      subscriptionsContext.selectionStatus !== ContextStatus.LOADING
    ) {
      subscriptionsContext.fetchAndSelectById(subscriptionId);
      plansContext.fetch();
    }

    if (
      jobsContext.status !== ContextStatus.LOADING ||
      jobsContext.scopedSubscriptionId !== subscriptionId
    ) {
      jobsContext.fetch(subscriptionId, null);
    }

    if (
      smsContext.status !== ContextStatus.LOADING ||
      smsContext.scopedSubscriptionId !== subscriptionId
    ) {
      smsContext.fetch(subscriptionId);
    }

    if (
      notificationsContext.status !== ContextStatus.LOADING ||
      notificationsContext.scopedSubscriptionId !== subscriptionId
    ) {
      notificationsContext.fetch(subscriptionId, true);
    }

    if (aiContext.status !== ContextStatus.LOADING) {
      aiContext.fetch(subscriptionId);
    }

    const fetchRelatedCompany = async () => {
      const companies = await getCompaniesBySubscriptionId(subscriptionId);
      if (companies.length > 0) {
        setRelatedCompany(companies[0]);
      }
    };
    fetchRelatedCompany();

    return () => {
      subscriptionsContext.reset();
      jobsContext.reset();
      smsContext.reset();
      notificationsContext.reset()
      aiContext.reset()
    };
  }, []);

  useEffect(() => {
    if (notificationsContext.exportStatus === ContextStatus.ERROR) {
      message.error(
        "Une erreur est survenue lors de l'export des notifications. Veuillez réessayer ultérieurement."
      );
    } else if (notificationsContext.exportStatus === ContextStatus.SUCCESS) {
      message.success('Les notifications ont bien été exportées.');
    }
  }, [notificationsContext.exportStatus]);

  useEffect(() => {
    if (jobsContext.scopedSubscriptionId) {
      jobsContext.fetchActionChart();
    }
  }, [jobsContext.scopedSubscriptionId]);


  const handleTabChange = (key: string) => {
    setActiveTab(key);
    setSearchParams({ tab: key });
  };

  const qualificationData = subscriptionsContext.qualificationDistribution || {
    interested: 0,
    considering: 0,
    not_interested: 0,
    not_responsive: 0,
    not_available: 0,
  };

  const totalContactsEngaged =
    subscriptionsContext.selectionStats?.engagedCount ?? 0;

  // Return string array with XX/XX values for all credits keys
  const plan = plansContext.items.find(
    (item) => item.id === subscriptionsContext.selected?.idPlan
  );
  const totalCredits = Object.entries(
    subscriptionsContext.selectionStats?.credits ?? {
      sms: 0,
      minutes: 0,
      contacts: 0,
    }
  )
    .sort((a, b) => b[0].localeCompare(a[0]))
    .map(([k, v], i, array) => {
      // If plan isnt custom and plan.credits[k] === 0 -> don't show it
      if (
        subscriptionsContext.selected?.idPlan !== 'c'
        && !(plan?.credits[
          k as unknown as keyof {
            sms: number;
            minutes: number;
            contacts: number;
          }
        ] ?? 0
        )
      )
        return null;

      // If plan is custom and subs.credits[k] === 0 -> don't show it
      if (subscriptionsContext.selected?.idPlan === 'c' && v === 0) return null;

      const planCredit = plan?.credits[
        k as unknown as keyof {
          sms: number;
          minutes: number;
          contacts: number;
        }
      ] ?? 0;

      return {
        key: k,
        icon: creditTypeAsString(k) ?? k,
        text: `${formatCredit(v || 0)} ${subscriptionsContext.selected?.idPlan !== 'c'
            ? `/ ${formatCredit(planCredit || 0)}`
            : ''
          }`,
      };
    })
    .filter((credit): credit is NonNullable<typeof credit> => credit !== null);

  const statsItems = [
    {
      icon: <ContactsOutlined />,
      title: 'Contacts',
      value: formatNumber(
        subscriptionsContext.selectionStats?.contactsCount ?? 0
      ),
      info: `(${totalContactsEngaged} contacts engagés)`,
      pagePath: `${SUBSCRIPTIONS_PAGE_PATH}/${subscriptionId}${CONTACTS_PAGE_PATH}`,
      isCondifential: false,
    },
    {
      icon: <MessageOutlined />,
      title: 'Reçus & envoyés',
      value: formatNumber(subscriptionsContext.selectionStats?.smsCount ?? 0),
      onClick: () => {
        handleTabChange(TAB_KEYS.sms);
      },
      isCondifential: false,
    },
    {
      icon: <ClockCircleOutlined />,
      title: 'Messages planifiés',
      value: formatNumber(subscriptionsContext.selectionStats?.jobsCount ?? 0),
      onClick: () => {
        handleTabChange(TAB_KEYS.scheduledMessages);
      },
      isCondifential: false,
    },
    {
      icon: <BankOutlined />,
      title: 'Coût total SMS',
      value: formatNumber(
        Math.floor(subscriptionsContext.selectionStats?.totalCostSms ?? 0) + '€'
      ),
      pagePath: CONTACTS_PAGE_PATH,
      isCondifential: true,
    },
    {
      icon: <BellOutlined />,
      title: 'Notification(s) en attente',
      value: formatNumber(
        subscriptionsContext.selectionStats?.notificationsPendingCount ?? 0
      ),
      onClick: () => {
        handleTabChange(TAB_KEYS.notifications);
      },
      isCondifential: false,
      color:
        (subscriptionsContext.selectionStats?.notificationsPendingCount ??
          0) === 0
          ? 'green'
          : (subscriptionsContext.selectionStats?.notificationsPendingCount ??
            0) < 6
            ? 'orange'
            : 'red',
    },
    {
      icon: <BellOutlined />,
      title: 'Notification(s) total',
      value: formatNumber(
        subscriptionsContext.selectionStats?.notficationsCount ?? 0
      ),
      onClick: () => {
        handleTabChange(TAB_KEYS.notifications);
      },
      isCondifential: false,
    },
    {
      icon: <StarOutlined />,
      title: 'Crédits restants',
      value: (
        <Flex align="center" justify="space-between" gap={10} wrap="wrap">
          <Flex vertical gap={6}>
            {totalCredits.length > 0
              ? totalCredits.map((credit, i, array) => (
                <Flex key={credit.key} align="center" gap={6}>
                  <span
                    role="img"
                    aria-label={credit.key}
                    style={{
                      fontSize: array.length > 1 ? '0.75rem' : '1.75rem',
                    }}
                  >
                    {credit.icon}{' '}
                  </span>
                  <span
                    style={{ fontSize: array.length > 1 ? '1rem' : '2rem' }}
                  >
                    {credit.text}
                  </span>
                </Flex>
              ))
              : '0'}
          </Flex>
          <span
            className="overview-page-stats__info"
            style={{
              textAlign: 'right',
              fontWeight: 100,
              fontSize: '.875rem',
              flex: '1 0 auto',
            }}
          >
            {subscriptionsContext.selected?.idPlan !== 'c' ? '(/mois)' : ''}
          </span>
        </Flex>
      ),
      pagePath: CONTACTS_PAGE_PATH,
      isCondifential: false,
    },
    {
      icon: <EuroCircleOutlined />,
      title: 'Coût moyen SMS',
      value:
        formatNumber(
          round2digits(
            (subscriptionsContext.selectionStats?.totalCostSms || 0) /
            (subscriptionsContext.selectionStats?.smsCount || 0)
          )
        ) + '€',
      pagePath: CONTACTS_PAGE_PATH,
      isCondifential: true,
    },
    {
      icon: <ContactsOutlined />,
      title: 'Coût par contact',
      value:
        formatNumber(
          round2digits(
            (subscriptionsContext.selectionStats?.totalCostSms || 0) /
            (subscriptionsContext.selectionStats?.contactsCount || 0)
          )
        ) + '€',
      pagePath: CONTACTS_PAGE_PATH,
      isCondifential: true,
    },
    {
      icon: <LineChartOutlined />,
      title: 'Taux de réponse',
      value: subscriptionsContext.selectionStats?.replyRate
        ? `${subscriptionsContext.selectionStats.replyRate}%`
        : 'N/A',
      pagePath: CONTACTS_PAGE_PATH,
      isCondifential: true,
    },
  ];

  // config chart
  const chartOptions = {
    chart: {
      type: 'bar' as const,
      stacked: true,
      stackType: '100%',
      height: 80,
    },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: '50%',
      },
    },
    xaxis: {
      max: 100,
      labels: {
        show: false,
      },
      categories: [''],
    },
    yaxis: {
      labels: {
        show: false,
      },
    },
    tooltip: {
      y: {
        formatter: (val: number) => `${val} contacts`,
      },
    },
    colors: ['#3dbea8', '#531dab', '#b7b7b7', '#f0e289', '#FFA500'],
  };

  const chartSeries = [
    {
      name: 'Intéressés',
      data: [qualificationData.interested],
    },
    {
      name: 'En réflexion',
      data: [qualificationData.considering],
    },
    {
      name: 'À venir',
      data: [qualificationData.not_available],
    },
    {
      name: 'Sans réponse',
      data: [qualificationData.not_responsive],
    },
    {
      name: 'Inactifs', // Pas intéressés
      data: [qualificationData.not_interested],
    },
  ];

  // Color mapping for each job type
  const getJobTypeColor = (jobType?: JobType): string => {
    if (!jobType) return '#808080'; // Default gray for unknown types

    switch (jobType) {
      case JobType.firstContact:
        return '#3dbea8';
      case JobType.followUp30Days:
      case JobType.followUp90Days:
      case JobType.followUp180Days:
      case JobType.followUp365Days:
        return '#531dab';
      case JobType.followUpToDo:
        return '#f0e289';
      case JobType.followUpDone:
        return '#32CD32';
      case JobType.reply:
        return '#FFA500'; 
      case JobType.reminderNoresponse:
        return '#00BFFF';
      default:
        return '#808080';
    }
  };

  // Generate colors array based on series data
  const getActionChartColors = () => {
    if (Object.keys(jobsContext.actionChartData).length === 0) {
      return ['#00E396', '#6737ff', '#FF4560'];
    }

    return jobsContext.actionChartData.series.map((serie) => {
      return getJobTypeColor(serie?.jobType);
    });
  };

  // config action chart
  const actionChartOptions = {
    chart: {
      type: 'bar',
      height: 350,
      stacked: true,
      toolbar: {
        show: true,
      },
    },
    xaxis: {
      categories: jobsContext.actionChartData.categories,
      title: {
        text: '30 prochains jours',
      },
    },
    yaxis: {
      title: {
        text: "Nombre d'actions",
      },
    },
    annotations: {
      yaxis: [
        {
          y: 200, // Valeur seuil alerte
          borderColor: 'orange',
          label: {
            text: "Seuil d'alerte",
            style: {
              color: '#fff',
              background: 'orange',
            },
          },
        },
      ],
    },
    plotOptions: {
      bar: {
        horizontal: false,
        borderRadius: 5,
        borderRadiusWhenStacked: 'last',
      },
    },
    dataLabels: {
      enabled: true,
      style: {
        fontSize: '10px',
        colors: ['#fff'],
      },
      offsetY: 2,
    },
    tooltip: {
      shared: true,
      intersect: false,
    },
    colors: getActionChartColors(),
    legend: {
      position: 'top',
    },
    fill: {
      opacity: 1,
    },
  };

  const handleActionChartSeries = () => {
    // Avoid passing `series: []` to ApexCharts (it can crash when combined with annotations)
    const series = jobsContext.actionChartData?.series ?? [];
    return series.length > 0
      ? series
      : [
          {
            name: 'Actions',
            data: [0],
          },
        ];
  };

  return (
    <PageScaffold
      title={subscriptionsContext.selected?.name ?? "Détail de l'abonnement"}
      subtitle={"Consultez et modifiez les informations de l'abonnement"}
      breadcrumbItems={[
        {
          target: SUBSCRIPTIONS_PAGE_PATH,
          title: 'Abonnements',
        },
        {
          title: subscriptionsContext.selected?.name || '',
        },
      ]}
    >
      <>
        <SubscriptionRelativesLinks company={relatedCompany} subscription={subscriptionsContext.selected ? subscriptionsContext.selected.toFirestorePatch() as ISubscription : null} plan={plan} />

        {aiContext.item?.status === AIStatus.pausedFull ? (
          <Alert
            message="L'IA est en pause complète"
            description="Tous les messages automatisés sont suspendus pour cet abonnement. Aucun SMS ne sera envoyé jusqu'à la reprise de l'IA."
            type="error"
            style={{ marginTop: '10px', marginBottom: '10px' }}
            banner
            closable
            icon={<PauseOutlined />}
          />
        ) : aiContext.item?.status === AIStatus.pausedNewcontacts ? (
          <Alert
            message="L'IA est en pause partielle"
            description="Seuls les nouveaux contacts non engagés ne recevront pas de messages. Les contacts engagés continuent d'en recevoir."
            type="warning"
            style={{ marginTop: '10px', marginBottom: '10px' }}
            banner
            closable
            icon={<PauseOutlined style={{ color: '#fa8c16' }} />}
          />
        ) : null}

        {subscriptionsContext.selected?.planEnd.isBefore(dayjs(new Date())) ? (
          <Alert
            message="Cet abonnement n'est plus actif car sa date de fin est dépassée."
            type="error"
            style={{ marginTop: '10px', marginBottom: '10px' }}
            banner
            closable
          />
        ) : null}

        {subscriptionsContext.selected?.idScenarios.length === 0 ? (
          <Alert
            message={
              'Veuillez ajouter au moins un scénario à ce client avant de lui importer des contacts.'
            }
            type="warning"
            style={{ marginTop: '10px', marginBottom: '10px' }}
            banner
            closable
          />
        ) : null}

        {subscriptionsContext.selected?.sender.length === 0 ? (
          <Alert
            message="La configuration de cet abonnement n'est pas terminée : aucun numéro d'envoyeur n'a été ajouté."
            type="warning"
            style={{ marginTop: '10px', marginBottom: '10px' }}
            banner
            closable
          />
        ) : null}
        {subscriptionsContext.selected?.status === SubscriptionStatus.CANCELLING ? (
          <Alert
            message={`Cet abonnement est en cours de résiliation. Il sera résilié le ${dayjs(subscriptionsContext.selected?.planEnd).format('DD/MM/YYYY')}.`}
            type="warning"
            style={{ marginTop: '10px', marginBottom: '10px' }}
            banner
            closable
          />
        ) : null}

        <SubscriptionEditionFormProvider>
          <Tabs
            defaultActiveKey={activeTab}
            activeKey={activeTab}
            onChange={handleTabChange}
            items={[
              {
                key: TAB_KEYS.stats,
                label: 'Statistiques',
                icon: <BarChartOutlined />,
                children: (
                  <>
                    <div
                      className="overview-page-stats"
                      style={{
                        marginBottom: '16px',
                      }}
                    >
                      {statsItems.map((item, index) => (
                        <StatsTile
                          key={index}
                          isLoading={
                            subscriptionsContext.status === ContextStatus.LOADING
                          }
                          item={item}
                          isConfidential={item.isCondifential}
                        />
                      ))}
                    </div>

                    <h3>Filtres</h3>

                    <div
                      style={{
                        marginBottom: '1rem',
                        display: 'flex',
                        gap: '1rem',
                      }}
                    >
                      <Tooltip title="Période pour l'intérêt des prospects">
                        <RangePicker
                          placeholder={['Début', 'Fin']}
                          format="DD/MM/YYYY"
                          value={period}
                          onChange={(dates) => {
                            if (dates) {
                              const [beginDate, endDate] = dates;
                              setPeriod([beginDate, endDate]);
                            } else {
                              setPeriod([null, null]);
                            }
                          }}
                        />
                      </Tooltip>
                      <Select
                        style={{ width: 250 }}
                        placeholder="Tous les scénarios"
                        value={scenarioSelected}
                        options={[
                          {
                            value: '',
                            label: 'Tous les scénarios',
                          },
                          ...subscriptionScenarios.map((scenario) => {
                            return {
                              value: scenario.id,
                              label: scenario.name,
                            };
                          }),
                        ]}
                        onChange={(value) => setScenarioSelected(value)}
                      />
                      <Select
                        style={{ width: 250 }}
                        placeholder="Tous les collaborateurs"
                        value={collaboratorSelected}
                        options={[
                          {
                            value: '',
                            label: 'Tous les collaborateurs',
                          },
                          ...subscriptionsContext.subscriptionUsers.map(
                            (user) => {
                              return {
                                value: user.uid,
                                label: `${user.firstname} ${user.lastname}`,
                              };
                            }
                          ),
                        ]}
                        onChange={(value) => setCollaboratorSelected(value)}
                      />
                      {subscriptionsContext.selected?.sender && subscriptionsContext.selected.sender.length > 0 && (
                        <Select
                          style={{ width: 250 }}
                          placeholder="Tous les numéros"
                          value={senderSelected}
                          disabled={subscriptionsContext.selected.sender.length === 1}
                          options={[
                            {
                              value: null,
                              label: 'Tous les numéros',
                            },
                            ...subscriptionsContext.selected.sender.map(
                              (sender) => {
                                return {
                                  value: sender,
                                  label: `+${sender}`,
                                };
                              }
                            ),
                          ]}
                          onChange={(value) => setSenderSelected(value)}
                        />
                      )}
                    </div>

                    <Card
                      title={<Flex align="center" gap={6}><BulbOutlined style={{ marginRight: 6 }} /> Intérêt des prospects qualifiés par l'IA</Flex>}
                      style={{ marginBottom: '8px', position: 'relative' }}
                    >
                      {smsContext.status === ContextStatus.LOADING ? (
                        <Skeleton active paragraph={{ rows: 4 }} />
                      ) : (
                        <>
                          <ReactApexChart
                            options={chartOptions as ApexOptions}
                            series={chartSeries}
                            type="bar"
                            height={200}
                          />
                          {qualificationData?.interested === 0 &&
                            qualificationData?.considering === 0 &&
                            qualificationData?.not_interested === 0 &&
                            qualificationData?.not_responsive === 0 &&
                            qualificationData?.not_available === 0 && (
                              <div
                                style={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  width: '100%',
                                  height: '100%',
                                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  zIndex: 1,
                                }}
                              >
                                <Alert
                                  message="Non disponible"
                                  description="Statistique non disponible car aucun prospect n'a été contacté pour le moment."
                                  type="info"
                                  showIcon
                                  style={{ textAlign: 'center', maxWidth: '80%' }}
                                />
                              </div>
                            )}
                        </>
                      )}
                    </Card>
                    <div className="overview-page-stats__actions">
                      <Card>
                        <h2><ClockCircleOutlined style={{ marginRight: 6 }} /> Messages planifiés</h2>
                        {(jobsContext.chartStatus !== ContextStatus.LOADING &&
                          jobsContext.actionChartData?.categories?.length > 0) ? (
                          <ReactApexChart
                            options={actionChartOptions as ApexOptions}
                            series={handleActionChartSeries()}
                            type="bar"
                            height={350}
                          />
                        ) : <Spin />}
                      </Card>
                    </div>
                  </>
                ),
              },
              {
                key: TAB_KEYS.generalInformation,
                label: 'Informations générales',
                icon: <InfoCircleOutlined />,
                children: (
                  selectionMismatch ||
                    subscriptionsContext.selected == null ||
                    aiContext.item == null ? (
                    <Card loading />
                  ) : (
                    <SubscriptionDetails
                      subscription={subscriptionsContext.selected}
                      companyId={relatedCompany?.id}
                      billingPeriodEnd={relatedCompany?.billing?.period_end ?? null}
                    />
                  )
                ),
              },
              {
                key: TAB_KEYS.users,
                label: 'Utilisateurs',
                icon: <UserOutlined />,
                children: (
                  <div className="list-container">
                    <div className="list-container__controls">
                      <h2>
                        <UserOutlined style={{ marginRight: 6 }} /> Utilisateurs (
                        {subscriptionsContext.subscriptionUsers.length || '-'})
                      </h2>
                      <div>
                        <Button
                          style={{ marginRight: '8px' }}
                          type="default"
                          onClick={() =>
                            navigate(
                              '/users/new?type=user&subscriptionId=' +
                              subscriptionId
                            )
                          }
                        >
                          Créer un nouvel utilisateur
                          <UserAddOutlined />
                        </Button>
                        <Button
                          type="primary"
                          onClick={() => setOpenModalAddUser(true)}
                        >
                          Ajouter utilisateur existant
                          <PlusCircleOutlined />
                        </Button>
                      </div>
                    </div>
                    <SubscriptionUsersList
                      users={subscriptionsContext.subscriptionUsers}
                    />
                  </div>
                ),
              },
              {
                key: TAB_KEYS.notifications,
                label: 'Notifications',
                icon: <BellOutlined />,
                children: (
                  <div className="list-container">
                    <div className="list-container__controls">
                      <h2><BellOutlined style={{ marginRight: 6 }} /> Notification(s) en attente</h2>
                      <div>
                        <Button
                          type="primary"
                          loading={
                            notificationsContext.exportStatus ===
                            ContextStatus.LOADING
                          }
                          style={{ marginRight: 12 }}
                          disabled={notificationsContext.count === 0}
                          onClick={() => {
                            notificationsContext.exportNotifs();
                          }}
                        >
                          <DownloadOutlined />
                          Exporter
                        </Button>
                        <Button
                          type="primary"
                          onClick={() =>
                            notificationsContext.fetch(subscriptionId, true)
                          }
                        >
                          <ReloadOutlined />
                          Rafraîchir
                        </Button>
                      </div>
                    </div>
                    <NotificationsList
                      scopedSubscriptionId={subscriptionId}
                      pendingOnly={true}
                    />
                  </div>
                ),
              },
              {
                key: TAB_KEYS.scheduledMessages,
                label: 'Messages planifiés',
                icon: <PushpinOutlined />,
                children: (
                  <div className="list-container">
                    <div className="list-container__controls">
                      <h2><ClockCircleOutlined style={{ marginRight: 6 }} /> Messages planifiés</h2>
                      <Button
                        type="primary"
                        onClick={() => jobsContext.fetch(subscriptionId, null)}
                      >
                        <ReloadOutlined />
                      </Button>
                    </div>
                    <JobsList
                      scopedSubscriptionId={subscriptionId}
                      scopedContactId={null}
                    />
                  </div>
                ),
              },
              {
                key: TAB_KEYS.sms,
                label: 'SMS',
                icon: <MessageOutlined />,
                children: (
                  <div className="list-container">
                    <div className="list-container__controls">
                      <h2><MessageOutlined style={{ marginRight: 6 }} /> Reçus & envoyés</h2>
                      <Button
                        type="primary"
                        onClick={() => smsContext.fetch(subscriptionId)}
                      >
                        <ReloadOutlined />
                      </Button>
                    </div>
                    <SMSList scopedSubscriptionId={subscriptionId} />
                  </div>
                ),
              },
              // Display the tab only if partnerSystem is not empty, null, or undefined
              ...(subscriptionsContext.selected?.partnerSystem
                ? [
                  {
                    key: TAB_KEYS.crm,
                    label: `Passerelle ${partnersTypeAsString(
                      subscriptionsContext.selected
                        .partnerSystem as PartnersType
                    )}`,
                    icon: <DesktopOutlined />,
                    children: (
                      <div className="list-container">
                        {subscriptionsContext.selected === null ? (
                          <Card loading />
                        ) : (
                          <CRMDetails
                            subscription={subscriptionsContext.selected}
                          />
                        )}
                      </div>
                    ),
                  },
                ]
                : []),
              ...(plan?.product === EProductType.LEEDFLOW
                ? [
                  {
                    key: TAB_KEYS.logs,
                    label: 'Logs',
                    icon: <FileTextOutlined />,
                    children: (
                      <SubscriptionLogs subscriptionId={subscriptionId} />
                    ),
                  },
                ]
                : []),
              {
                key: TAB_KEYS.scenarios,
                label: 'Scénarios',
                icon: <ScheduleOutlined />,
                children: (
                  <div className="list-container">
                    <h2>🎞️ Scénarios</h2>
                    {subscriptionsContext.selected === null ? (
                      <Card loading />
                    ) : (
                      <SubscriptionScenariosList
                        subscription={subscriptionsContext.selected}
                      />
                    )}
                  </div>
                ),
              },
              {
                key: TAB_KEYS.tags,
                label: 'Tags',
                icon: <TagsOutlined />,
                children: (
                  <div className="list-container">
                    <TagsList subscriptionId={subscriptionId} filterCategories={[TagCategory.custom]} defaultCategory={TagCategory.custom} />
                  </div>
                ),
              },
              {
                key: TAB_KEYS.costs,
                label: 'Coûts',
                icon: <WalletOutlined />,
                children: (
                  <div className="list-container">
                    <SubscriptionCost
                      subscriptionId={subscriptionId}
                      currentLeedbucks={subscriptionsContext.selected?.credits?.leedbucks ?? 0}
                      totalLeedbucks={plan?.credits?.leedbucks ?? 0}
                      planStart={subscriptionsContext.selected?.planStart?.valueOf()}
                      planEnd={subscriptionsContext.selected?.planEnd?.valueOf()}
                      creditsPeriodEnd={subscriptionsContext.selected?.creditsPeriodEnd}
                    />
                  </div>
                ),
              },
            ]}
          />
        </SubscriptionEditionFormProvider>

        {/* Add user modal */}
        <Modal
          title="Ajout d'un utilisateur"
          open={openModalAddUser}
          onOk={async () => {
            await subscriptionsContext.addUser(emailAddUser);
            setEmailAddUser('');
            setOpenModalAddUser(false);
          }}
          onCancel={() => {
            setEmailAddUser('');
            setOpenModalAddUser(false);
          }}
          loading={
            subscriptionsContext.statusAddRemoveUser === ContextStatus.LOADING
          }
          okText="Ajouter"
          cancelText="Annuler"
        >
          <Alert
            description="L'utilisateur devra se déconnecter se reconnecter de son espace pour accéder à l'abonnement"
            type="info"
            showIcon
          />
          <Input
            value={emailAddUser}
            onChange={(e) => setEmailAddUser(e.target.value)}
            placeholder="utilisateur@email.com"
            style={{ marginTop: '16px' }}
          />
        </Modal>
      </>
    </PageScaffold>
  );
};

export default SubscriptionDetailPage;